import React, { useEffect, useState } from 'react';
import { Button } from '@mui/material';
import Rating from '@mui/material/Rating';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { auth } from '../../helper/firebase.helper';
import Loader from '../../components/loader/Loader';
import TopNav from '../../components/top-nav/TopNav';
import { getFeedback, saveFeedback } from './feedback.service';

const Feedback = () => {
  const { feedbackId } = useParams();
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const isLoggedIn = !!auth?.currentUser;

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      const loggedIn = !!auth?.currentUser;
      if (loggedIn && feedbackId) {
        const existing = await getFeedback(feedbackId);
        if (existing) {
          if (typeof existing.stars === 'number') {
            setStars(existing.stars);
          }
          if (typeof existing.comment === 'string') {
            setComment(existing.comment);
          }
        }
      }
    } catch (error) {
      console.log(error.message);
      toast.error('Error fetching feedback data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedback();
  }, [feedbackId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!feedbackId) {
      toast.error('Feedback id is missing');
      return;
    }
    if (!stars || stars < 1) {
      toast.error('Please select a star rating before submitting.');
      return;
    }
    setLoading(true);
    try {
      await saveFeedback(feedbackId, {
        stars,
        comment: comment.trim()
      });
      toast.success('Feedback saved successfully');
      setIsSubmitted(true);
    } catch (error) {
      console.log(error.message);
      toast.error('Feedback cannot be submitted. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <TopNav />
          {isSubmitted ? (
            <div className="feedback-shell">
              <div className="feedback-wrapper">
                <div className="feedback-card feedback-card--center">
                  {/* <div className="feedback-icon">🎉</div> */}
                  <h3 className="feedback-title margin-top--lg">Thank you for your feedback!</h3>
                  <p className="feedback-subtitle">
                    {isLoggedIn ? (
                      <>
                        You can view your history in the dashboard{' '}
                        <Link
                          to="/dashboard/history"
                          className="feedback-link"
                          style={{ color: 'blue' }}>
                          here
                        </Link>
                        .
                      </>
                    ) : (
                      <>
                        You can go to the home page{' '}
                        <Link to="/" className="feedback-link" style={{ color: 'blue' }}>
                          here
                        </Link>
                        .
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="feedback-shell">
              <div className="feedback-wrapper">
                <div className="feedback-card">
                  <h3 className="feedback-title">Share your experience</h3>
                  <form onSubmit={handleSubmit}>
                    <div className="feedback-field">
                      <div className="feedback-label-row">
                        <label className="feedback-label">
                          Rating<span className="feedback-required">*</span>
                        </label>
                        <span className="feedback-label-hint">1–5 stars</span>
                      </div>
                      <Rating
                        name="feedback-stars"
                        value={stars}
                        onChange={(_, value) => setStars(value || 0)}
                        size="large"
                      />
                    </div>

                    <div className="feedback-field">
                      <div className="feedback-label-row">
                        <label className="feedback-label">Comments</label>
                        <span className="feedback-label-hint">(optional)</span>
                      </div>
                      <textarea
                        rows={4}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Tell us what went well or what we can improve…"
                        className="feedback-textarea"
                      />
                    </div>

                    <div className="feedback-actions">
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="medium"
                        style={{ minWidth: '120px', textTransform: 'none' }}>
                        Submit feedback
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <style jsx>{`
        .feedback-shell {
          background: #f5f7fb;
          min-height: 100vh;
        }

        .feedback-wrapper {
          max-width: 720px;
          margin: 0 auto;
          padding: 80px 16px 40px;
          display: flex;
          justify-content: center;
          align-items: flex-start;
        }

        .feedback-card {
          width: 100%;
          max-width: 520px;
          background: #ffffff;
          border-radius: 16px;
          padding: 60px 20px 50px 20px;
          box-shadow: 0 18px 45px rgba(23, 43, 77, 0.2);
        }

        .feedback-card--center {
          text-align: center;
        }

        .feedback-icon {
          font-size: 32px;
          margin-bottom: 8px;
        }

        .feedback-title {
          font-size: 22px;
          font-weight: 600;
          margin: 0 0 6px;
          color: #111827;
        }

        .feedback-subtitle {
          margin: 0 0 18px;
          font-size: 14px;
          line-height: 1.5;
          color: #6b7280;
        }

        .feedback-link {
          color: #2563eb;
          text-decoration: none;
          font-weight: 500;
        }

        .feedback-link:hover {
          text-decoration: underline;
        }

        .feedback-field {
          margin-top: 18px;
        }

        .feedback-label-row {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          margin-bottom: 6px;
        }

        .feedback-label {
          font-size: 14px;
          font-weight: 500;
          color: #111827;
        }

        .feedback-required {
          color: #ef4444;
          margin-left: 2px;
        }

        .feedback-label-hint {
          font-size: 12px;
          color: #9ca3af;
        }

        .feedback-textarea {
          width: 100%;
          border-radius: 3px;
          border: 1px solid #e5e7eb;
          padding: 3px 0px 0px 3px;
          font-size: 14px;
          resize: vertical;
          min-height: 96px;
          font-family: inherit;
          outline: none;
          background: #f9fafb;
          transition:
            border-color 0.16s ease,
            box-shadow 0.16s ease,
            background-color 0.16s ease;
        }

        .feedback-textarea:focus {
          border-color: #007aff;
          box-shadow: 0 0 0 1px rgba(37, 99, 235, 0.15);
          background: #ffffff;
        }

        .feedback-textarea::placeholder {
          color: #9ca3af;
        }

        .feedback-actions {
          margin-top: 24px;
          display: flex;
          justify-content: flex-end;
        }

        @media only screen and (max-width: 600px) {
          .feedback-wrapper {
            padding: 72px 12px 32px;
          }

          .feedback-card {
            padding: 20px 16px 24px;
            border-radius: 14px;
          }

          .feedback-title {
            font-size: 20px;
          }
        }
      `}</style>
    </>
  );
};

export default Feedback;
